(() => {
  if (customElements.get('color-swatch-map-store')) return;

  const STORAGE_PREFIX = 'color-swatch-map:';

  const parseMap = (mapString) => {
    const entries = {};

    if (!mapString?.trim()) return entries;

    for (const segment of mapString.split('|')) {
      if (!segment) continue;

      const separatorIndex = segment.indexOf('::');
      if (separatorIndex === -1) continue;

      const key = segment.slice(0, separatorIndex);
      const url = segment.slice(separatorIndex + 2);

      if (key && url) entries[key] = url;
    }

    return entries;
  };

  const formatSwatchValue = (value) => {
    if (!value) return '';

    if (
      value.startsWith('url(') ||
      value.startsWith('#') ||
      value.startsWith('linear-gradient')
    ) {
      return value;
    }

    return `url(${value})`;
  };

  const encodeKey = (value) => encodeURIComponent(String(value).trim().toLowerCase());

  class ColorSwatchMapStore extends HTMLElement {
    constructor() {
      super();
      this.mapEntries = {};
      this.onPageShow = this.onPageShow.bind(this);
      this.fetchMissingSwatches = this.fetchMissingSwatches.bind(this);
    }

    connectedCallback() {
      const storedEntries = this.readSessionStorage();
      const serverEntries = parseMap(
        this.querySelector('[data-js-color-swatch-map]')?.textContent
      );

      this.mapEntries = { ...storedEntries, ...serverEntries };
      this.applyToFacets();
      this.persistToSessionStorage();
      window.addEventListener('pageshow', this.onPageShow);
      window.requestIdleCallback?.(() => this.fetchMissingSwatches()) ??
        setTimeout(this.fetchMissingSwatches, 0);
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onPageShow);
    }

    onPageShow(event) {
      if (!event.persisted) return;

      this.mapEntries = {
        ...this.readSessionStorage(),
        ...parseMap(this.querySelector('[data-js-color-swatch-map]')?.textContent),
      };
      this.applyToFacets({ forceRepaint: true });
      window.requestIdleCallback?.(() => this.fetchMissingSwatches()) ??
        setTimeout(this.fetchMissingSwatches, 0);
    }

    get storageKey() {
      return `${STORAGE_PREFIX}${window.location.pathname}`;
    }

    get sectionId() {
      return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    }

    readSessionStorage() {
      try {
        const stored = sessionStorage.getItem(this.storageKey);
        if (!stored) return {};

        return JSON.parse(stored);
      } catch {
        return {};
      }
    }

    persistToSessionStorage() {
      if (!Object.keys(this.mapEntries).length) return;

      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.mapEntries));
      } catch {
        /* ignore quota errors */
      }
    }

    saveComponentState() {
      return { mapEntries: this.mapEntries };
    }

    loadComponentState(state) {
      this.mapEntries = { ...this.mapEntries, ...(state?.mapEntries || {}) };
      this.applyToFacets();
      this.persistToSessionStorage();
      window.requestIdleCallback?.(() => this.fetchMissingSwatches()) ??
        setTimeout(this.fetchMissingSwatches, 0);
    }

    mergeMapString(mapString) {
      this.mapEntries = { ...this.mapEntries, ...parseMap(mapString) };
      this.persistToSessionStorage();
    }

    resolveUrl(label) {
      const keys = [label.dataset.colorKey, label.dataset.colorValueKey].filter(Boolean);

      for (const key of keys) {
        const url = this.mapEntries[key];
        if (url) return url;
      }

      const input = document.getElementById(label.getAttribute('for'));
      if (!input?.value) return null;

      return this.mapEntries[encodeKey(input.value)] || null;
    }

    hasImageSwatch(value) {
      return value.includes('url(') || value.includes('linear-gradient(');
    }

    async fetchMissingSwatches() {
      const section = this.closest('.shopify-section');
      const sectionId = this.sectionId;
      if (!section || !sectionId) return;

      const missingInputs = [
        ...new Set(
          [...section.querySelectorAll('[data-color-key]')]
            .filter((label) => {
              const currentValue = label.style.getPropertyValue('--swatch-color').trim();
              if (currentValue) return false;

              const input = document.getElementById(label.getAttribute('for'));
              return input?.name && input?.value;
            })
            .map((label) => document.getElementById(label.getAttribute('for')))
            .filter(Boolean)
        ),
      ];

      if (!missingInputs.length) return;

      await Promise.all(
        missingInputs.map(async (input) => {
          try {
            const url = new URL(window.location.pathname, window.location.origin);
            url.searchParams.set(input.name, input.value);
            url.searchParams.set('section_id', sectionId);

            const response = await fetch(url.toString());
            if (!response.ok) return;

            const mapString = new DOMParser()
              .parseFromString(await response.text(), 'text/html')
              .querySelector('[data-js-color-swatch-map]')?.textContent;

            if (mapString) this.mergeMapString(mapString);
          } catch {
            /* ignore network errors */
          }
        })
      );

      this.applyToFacets();
    }

    applyToFacets({ forceRepaint = false } = {}) {
      const section = this.closest('.shopify-section');
      if (!section || !Object.keys(this.mapEntries).length) return;

      section.querySelectorAll('[data-color-key]').forEach((label) => {
        const currentValue = label.style.getPropertyValue('--swatch-color').trim();
        const url = this.resolveUrl(label);

        if (forceRepaint && this.hasImageSwatch(currentValue)) {
          label.style.removeProperty('--swatch-color');
          label.offsetHeight;
          label.style.setProperty('--swatch-color', currentValue);
          return;
        }

        if (currentValue) return;
        if (!url) return;

        label.style.setProperty('--swatch-color', formatSwatchValue(url));
        label.classList.add('has-swatch-border');
      });
    }
  }

  customElements.define('color-swatch-map-store', ColorSwatchMapStore);
})();
